/**
 * Solana address validation for the send form.
 *
 * A Solana address is a 32-byte Ed25519 public key rendered in Base58, with no
 * version byte and no checksum, so validation is a Base58 decode followed by a
 * 32-byte length check. The gateway builds the unsigned Solana transfer message
 * server-side from the plain destination and decimal amount and signs it through
 * the Ed25519 FROST path, so this module validates the typed address only and
 * produces no payload.
 */

import { base58Decode } from './base58';

/** Solana amounts are denominated in lamports, with nine decimals to one SOL. */
export const SOLANA_DECIMALS = 9;

/** A Solana address decodes to exactly 32 bytes. */
const SOLANA_KEY_LEN = 32;

/**
 * Validate a Solana address. Returns an empty string when valid, or a short
 * reason when not, matching the form's per-field error convention.
 *
 * @param address The address as entered.
 * @returns An error message, or an empty string when valid.
 */
export function solanaAddressError(address: string): string {
  const value = typeof address === 'string' ? address.trim() : '';
  if (value === '') return 'Enter a recipient address.';
  // A Solana address is 32 to 44 Base58 characters; reject obvious non-Base58
  // shapes early before the decode so the message is specific.
  if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value)) {
    return 'That does not look like a Solana address.';
  }
  const bytes = base58Decode(value);
  if (!bytes || bytes.length !== SOLANA_KEY_LEN) {
    return 'A Solana address decodes to 32 bytes.';
  }
  return '';
}

/**
 * Whether a Solana address is well formed.
 *
 * @param address The address as entered.
 * @returns True when the address validates.
 */
export function isValidSolanaAddress(address: string): boolean {
  return solanaAddressError(address) === '';
}
